import { useState } from "react";
import { Check, ChevronDown, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useGroomingProjects } from "@/hooks/queries/use-grooming";
import { useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { groomingPath } from "@/lib/grooming/paths";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";
import { AddProjectDialog } from "./add-project-dialog";

interface ProjectSwitcherProps {
  /** The project in the URL, if any. */
  project: string | undefined;
}

/**
 * The title bar's project switcher (design `.proj`): the projects with
 * grooming set up; picking one opens its backlog. A project still being set
 * up (not in the list yet) stays shown as the current one.
 *
 * A global Admin also gets a separator and an "Add project…" item (ER-1678)
 * that opens the Add a project dialog; nobody else sees it, and the empty
 * text tells them to ask an Admin. The dialog sits beside the menu, not in
 * it, so closing the menu does not unmount it; it is mounted only while an
 * Admin has it open, so nobody else triggers its ADO read and reopening
 * retries a failed one.
 *
 * A "Project" label and the full name (UX-PM-22, frame 1a). The name is the
 * last thing in the title bar to give way: the search shrinks and collapses
 * first, and only then does a long name truncate with an ellipsis. The full
 * name is always in the trigger's tooltip (`title`) and accessible name
 * ("Project: <name>").
 */
export function ProjectSwitcher({ project }: ProjectSwitcherProps) {
  const navigate = useNavigate();
  const projects = useGroomingProjects();
  const { data: access } = useToolboxAccess();
  const isAdmin = access?.isAdmin === true;
  const [adding, setAdding] = useState(false);
  const names = (projects.data ?? []).map((p) => p.project);
  const current = project ?? names[0];
  const options =
    current && !names.some((name) => name.toLowerCase() === current.toLowerCase())
      ? [current, ...names]
      : names;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          type="button"
          aria-label={current ? `Project: ${current}` : "Choose a project"}
          title={current}
          className="inline-flex h-[26px] min-w-0 shrink items-center gap-1.5 whitespace-nowrap rounded-[7px] border border-[#27272A] bg-[#121215] px-[9px] text-[13px] font-medium text-[#FAFAFA] transition-colors hover:bg-[#141417] [-webkit-app-region:no-drag]"
        >
          <span className={cn("flex-none text-[10.5px] font-medium", TOOLBOX_TEXT.faint)}>
            Project
          </span>
          <span className="min-w-0 truncate" data-testid="project-switcher-name">
            {current ?? "Choose a project"}
          </span>
          <ChevronDown
            className={cn("h-[13px] w-[13px] flex-none", TOOLBOX_TEXT.faint)}
            aria-hidden="true"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[220px]">
          {options.length === 0 ? (
            <p
              className="px-2 py-1.5 text-sm text-muted-foreground"
              data-testid="project-switcher-empty"
            >
              {isAdmin
                ? "No project is set up yet. Choose Add project… to set one up."
                : "An Admin sets a project up. Ask an Admin to add it."}
            </p>
          ) : (
            options.map((name) => (
              <DropdownMenuItem
                key={name}
                className="gap-2"
                onClick={() => {
                  void navigate(groomingPath(name, "backlog"));
                }}
              >
                <span className="flex-1 truncate">{name}</span>
                {name.toLowerCase() === current?.toLowerCase() && (
                  <Check className="h-4 w-4 text-primary" aria-label="Current project" />
                )}
              </DropdownMenuItem>
            ))
          )}
          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2"
                data-testid="project-switcher-add"
                onClick={() => {
                  setAdding(true);
                }}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                <span>Add project…</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {isAdmin && adding ? <AddProjectDialog open onOpenChange={setAdding} /> : null}
    </>
  );
}
